import { action } from '@ember/object';
import { service } from '@ember/service';

import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';

import { fadeIn, fadeOut } from 'ember-animated/motions/opacity';
import move from 'ember-animated/motions/move';

import AnimatedContainer from 'ember-animated/components/animated-container';
import animatedEach from 'ember-animated/components/animated-each';

import { task, timeout as waiter } from 'ember-concurrency';

import { isTest } from '../../../../utils/is-testing';
import generateUUID from '../../../../utils/generate-uuid';

import Message from './message.gjs';
import styles from './container.module.scss';

export const DEFAULT_TIMEOUT = 3000;

export default class SpdsFeedbackToastContainer extends Component {
  isTest = isTest();
  styles = styles;

  @tracked toasts = [];

  @service alertManager;

  get event() {
    return this.args.local ? 'displayLocalToast' : 'displayToast';
  }

  constructor() {
    super(...arguments);
    this.#setupContainer();
  }

  #setupContainer() {
    let { alertManager, displayToast: handler, event } = this;

    alertManager.bindEventHandler({
      context: this,
      event,
      handler,
    });
  }

  // eslint-disable-next-line require-yield
  *transition({ insertedSprites, keptSprites, removedSprites }) {
    let y = window.innerHeight;

    for (let sprite of insertedSprites) {
      sprite.startAtPixel({ y });
      fadeIn(sprite, { duration: 300, to: 1 });
      move(sprite);
    }

    keptSprites.forEach(move);

    for (let sprite of removedSprites) {
      sprite.endAtPixel({ y });
      fadeOut(sprite, { duration: 200, to: 0 });
      move(sprite);
    }
  }

  displayToast(toast) {
    this.toasts = [...this.toasts, { id: generateUUID(), ...toast }];

    if (this.running) return;

    this.manageTimer();
  }

  async manageTimer() {
    let toast = this.toasts[0];
    let { timeout = DEFAULT_TIMEOUT } = toast;

    if (timeout > 0 && !this.isTest) {
      this.enqueueToastDismissal.perform(toast, timeout);
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.enqueueToastDismissal.cancelAll();
  }

  @(task(function* (toast, t) {
    this.running = true;
    yield waiter(t);
    this.autoDismiss(toast);
  }).enqueue())
  enqueueToastDismissal;

  @action
  autoDismiss(toast) {
    let index = this.toasts.findIndex(({ id }) => id === toast.id);

    this.toasts.splice(index, 1);
    this.toasts = [...this.toasts];

    if (index === 0 && this.toasts.length) {
      this.manageTimer();
    } else if (this.toasts.length === 0) {
      this.running = false;
    }
  }

  @action
  manualDismiss(toast) {
    toast?.onManualDismissal?.();
    this.autoDismiss(toast);
  }

  <template>
    <AnimatedContainer
      @tag="ul"
      class={{this.styles.component}}
      data-test-spds-feedback-toast-container={{if @local "local" "global"}}
      ...attributes
    >
      {{#if this.isTest}}
        {{#each this.toasts as |toast index|}}
          <Message @index={{index}} @toast={{toast}} @onManualDismiss={{this.manualDismiss}} />
        {{/each}}
      {{else}}
        {{#animatedEach this.toasts use=this.transition as |toast index|}}
          <Message @index={{index}} @toast={{toast}} @onManualDismiss={{this.manualDismiss}} />
        {{/animatedEach}}
      {{/if}}
    </AnimatedContainer>
  </template>
}
